/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  todos: {
    all: ["todos"] as const,
    list: <T extends object>(options: T) => ["todos", "list", options] as const,
    /** Variante paginada de `list` (límite incremental de la vista). */
    listPaged: <T extends object>(options: T, limit: number) =>
      ["todos", "list", options, limit] as const,
    /** Conteo de pendientes de una lista (badge). Derivada de `list` → se invalida con su prefijo. */
    listCount: <T extends object>(options: T) => ["todos", "list", options, "count"] as const,
    detail: (id: string | undefined) => ["todos", "detail", id] as const,
    /** Resolución list_id de un todo para el redirect de /todo/:id. */
    detailRedirect: (id: string | undefined) => ["todos", "detail-redirect", id] as const,
    stats: (userId: string | undefined) => ["todos", "stats", userId] as const,
    remindersPool: () => ["todos", "reminders-pool"] as const,
  },
  search: {
    all: ["search"] as const,
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    /** Stats de cola de subida (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** ¿Hay datos locales? (botón de limpieza en profile). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  lists: {
    all: ["lists"] as const,
    /** Organización (sharing) asociada a una lista. */
    organization: (listId: string | undefined) => ["lists", "organization", listId] as const,
    /** All lists with theme + group metadata for a user (sidebar / route). */
    customLists: (userId: string | undefined) => ["lists", "customLists", userId] as const,
    /** List groups for a user. */
    groups: (userId: string | undefined) => ["lists", "groups", userId] as const,
    /** Single list detail by id. */
    detail: (listId: string | undefined) => ["lists", "detail", listId] as const,
    /** Pending todo count per list_id (GROUP BY). */
    todoCounts: (userId: string | undefined) => ["lists", "todoCounts", userId] as const,
    /** Count of lists owned by user (profile stats). */
    count: (userId: string | undefined) => ["lists", "count", userId] as const,
    withCounts: (userId: string | undefined, limit?: number) =>
      ["lists", "withCounts", userId, limit] as const,
  },
  myDay: {
    all: ["myDay"] as const,
    todos: (dateKey: string) => ["myDay", "todos", dateKey] as const,
    suggestions: (dateKey: string) => ["myDay", "suggestions", dateKey] as const,
  },
  smartViews: {
    counts: (userId: string | undefined) => ["smartViews", "counts", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Catálogo completo de temas (getThemesQuery sin paginar; profile + list-dialog COMPARTEN caché). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  subtasks: {
    all: ["subtasks"] as const,
    byTodo: (todoId: string | undefined) => ["subtasks", todoId] as const,
  },
  attachments: {
    all: ["attachments"] as const,
    byTodo: (todoId: string) => ["attachments", todoId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync). */
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  recipes: {
    all: ["recipes"] as const,
    /** Todas las recetas del usuario, con filtro opcional (busqueda/etiqueta). */
    list: (userId: string | undefined, search?: string, tag?: string) =>
      ["recipes", "list", userId, search ?? "", tag ?? ""] as const,
    detail: (id: string | undefined) => ["recipes", "detail", id] as const,
    ingredients: (recipeId: string | undefined) => ["recipes", "ingredients", recipeId] as const,
    /** Etiquetas distintas del recetario (filtros). */
    tags: (userId: string | undefined) => ["recipes", "tags", userId] as const,
    /** Recetas en la papelera (soft-delete). */
    trashed: (userId: string | undefined) => ["recipes", "trashed", userId] as const,
    /** Historial de versiones LOCAL de una receta (bucket acotado). */
    versions: (recipeId: string | undefined) => ["recipes", "versions", recipeId] as const,
  },
  activity: {
    all: ["activity"] as const,
    /** Feed del hogar (bucket local activity_recent). */
    feed: (userId: string | undefined) => ["activity", "feed", userId] as const,
    /** Historial local de una entidad concreta. */
    entity: (userId: string | undefined, entityType: string, entityId: string) =>
      ["activity", "entity", userId, entityType, entityId] as const,
  },
  mealPlan: {
    all: ["mealPlan"] as const,
    /** Entradas del plan en un rango de fechas (semana). */
    week: (userId: string | undefined, startDate: string, endDate: string) =>
      ["mealPlan", "week", userId, startDate, endDate] as const,
    /** Entradas del plan que usan una receta concreta (interconexión). */
    byRecipe: (userId: string | undefined, recipeId: string | undefined) =>
      ["mealPlan", "byRecipe", userId, recipeId] as const,
  },
  insights: {
    all: ["insights"] as const,
    /** Entradas del plan en un rango (analítica multi-semana). */
    planRange: (userId: string | undefined, startDate: string, endDate: string) =>
      ["insights", "planRange", userId, startDate, endDate] as const,
    /** Recetas más planificadas (conteo por receta). */
    topRecipes: (userId: string | undefined, startDate: string, endDate: string) =>
      ["insights", "topRecipes", userId, startDate, endDate] as const,
  },
  shopping: {
    all: ["shopping"] as const,
    lists: (userId: string | undefined) => ["shopping", "lists", userId] as const,
    detail: (listId: string | undefined) => ["shopping", "detail", listId] as const,
    items: (listId: string | undefined) => ["shopping", "items", listId] as const,
    /** Organizaciones con las que esta compartida una lista. */
    organizations: (listId: string | undefined) => ["shopping", "organizations", listId] as const,
  },
  aisles: {
    all: (userId: string | undefined) => ["aisles", userId] as const,
  },
  pantry: {
    all: (userId: string | undefined) => ["pantry", userId] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
};
